import { API_BASE_URL } from './config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Image as ImageIcon } from 'lucide-react';
import secureStorage from './utils/secureStorage';

const API = API_BASE_URL;

const DisplayImages = () => {
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [exerciseName, setExerciseName] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const loadImages = async () => {
    try {
      const token = secureStorage.getToken();
      
      if (!token) {
        console.error('No authentication token found');
        setLoading(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Try to get exercise ID from localStorage (set by main app when viewing exercise)
      let exerciseId = secureStorage.getPreference('current-exercise-id');
      let exerciseName = secureStorage.getPreference('current-exercise-name');
      
      console.log('Exercise ID from localStorage:', exerciseId);
      console.log('Exercise name from localStorage:', exerciseName);
      
      // If not in localStorage, fall back to current exercise API
      if (!exerciseId) {
        console.log('No exercise ID found, fetching current exercise...');
        const exerciseResponse = await axios.get(`${API}/api/exercises/current`, { headers });
        console.log('Exercise response:', exerciseResponse.data);
        
        if (exerciseResponse.data) {
          exerciseId = exerciseResponse.data.id;
          exerciseName = exerciseResponse.data.exercise_name;
        }
      }
      
      if (exerciseId) {
        setExerciseName(exerciseName || 'Exercise Images');
        
        // Load images for this specific exercise
        console.log('Fetching images for exercise:', exerciseId);
        const imagesResponse = await axios.get(
          `${API}/api/images?exercise_id=${exerciseId}`,
          { headers }
        );
        
        console.log('Images response:', imagesResponse.data);
        console.log('Number of images:', imagesResponse.data?.length || 0);
        
        if (imagesResponse.data && imagesResponse.data.length > 0) {
          setImages(imagesResponse.data);
        } else {
          console.log('No images found for this exercise');
        }
      } else {
        console.log('No exercise ID available');
      }
      
      setLoading(false);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading images:', error);
      console.error('Error details:', error.response?.data || error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages();

    // Reload images every 120 seconds to get new uploads
    const reloadInterval = setInterval(() => {
      loadImages();
    }, 120000);

    return () => clearInterval(reloadInterval);
  }, []);

  useEffect(() => {
    if (images.length === 0) return;

    // Change image every 180 seconds (3 minutes)
    const slideInterval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % images.length;
        return nextIndex;
      });
    }, 180000);

    return () => clearInterval(slideInterval);
  }, [images.length]);

  if (loading) {
    return (
      <div className="w-screen h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading Images...</div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="w-screen h-screen bg-gray-900">
        <div className="bg-gray-800 border-b border-orange-500 px-6 py-4">
          <h1 className="text-3xl font-bold text-orange-500">EXRSIM - Image Display</h1>
          <p className="text-lg text-gray-400">{exerciseName}</p>
        </div>
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <div className="text-center">
            <ImageIcon className="h-24 w-24 text-gray-600 mx-auto mb-4" />
            <p className="text-white text-2xl">No Images Available</p>
          </div>
        </div>
      </div>
    );
  }

  const currentImage = images[currentImageIndex];

  return (
    <div className="w-screen h-screen bg-gray-900 overflow-hidden">
      {/* Header Bar */}
      <div className="bg-gray-800 border-b border-orange-500 px-6 py-4 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-orange-500">EXRSIM - Image Display</h1>
          <p className="text-lg text-gray-400">{exerciseName}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-400">Image {currentImageIndex + 1} of {images.length}</p>
          <p className="text-sm text-gray-400">Last Updated: {lastUpdate.toLocaleTimeString()}</p>
        </div>
      </div>

      {/* Image Display */}
      <div className="h-[calc(100vh-100px)] flex items-center justify-center p-8 bg-black">
        <div className="relative w-full h-full flex flex-col">
          {/* Image */}
          <div className="flex-1 flex items-center justify-center">
            <img
              src={currentImage.image_data}
              alt={currentImage.name || 'Image'}
              className="max-w-full max-h-full object-contain"
              style={{ transition: 'opacity 1s ease-in-out' }}
              onError={(e) => {
                console.error('Error loading image:', currentImage);
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EImage Error%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>

          {/* Image Info Bar */}
          <div className="bg-gray-800/90 backdrop-blur-sm px-6 py-4 rounded-lg mt-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  {currentImage.name || 'Untitled Image'}
                </h2>
                {currentImage.description && (
                  <p className="text-gray-300 text-lg">{currentImage.description}</p>
                )}
              </div>
              <div className="text-right">
                {currentImage.category && (
                  <p className="text-orange-500 text-lg font-semibold">{currentImage.category}</p>
                )}
                {currentImage.uploaded_at && (
                  <p className="text-gray-400 text-sm">
                    Uploaded: {new Date(currentImage.uploaded_at).toLocaleDateString()}
                  </p>
                )}
                {currentImage.exercise_name && (
                  <p className="text-gray-400 text-sm">
                    Exercise: {currentImage.exercise_name}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
        {images.map((_, index) => (
          <div
            key={`dot-${index}`}
            className={`w-3 h-3 rounded-full ${
              index === currentImageIndex ? 'bg-orange-500' : 'bg-gray-600'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default DisplayImages;
